import InlineSVG from "react-inlinesvg";
import { useState } from "react";
// @ts-ignore
import styled from "styled-components/macro";
import { SudoTooltip } from "../../sudokit/components/SudoTooltip";

export enum ArrowPosition {
  Top = "top",
  Bottom = "bottom",
  TopRight = "top-right",
  TopLeft = "top-left",
  BottomRight = "bottom-right",
  BottomLeft = "bottom-left",
  None = "none",
}

enum ArrowTypes {
  Top = "sticky-arrow-top.svg",
  Bottom = "sticky-arrow-bottom.svg",
  TopRight = "sticky-arrow-top-right.svg",
  TopLeft = "sticky-arrow-top-left.svg",
  BottomRight = "sticky-arrow-bottom-right.svg",
  BottomLeft = "sticky-arrow-bottom-left.svg",
}

interface ArrowConfig {
  file: ArrowTypes;
  transform: string;
  position: string;
}

const ARROW_CONFIGS: Record<Exclude<ArrowPosition, "none">, ArrowConfig> = {
  [ArrowPosition.Top]: {
    file: ArrowTypes.Top,
    transform: "scale(.8)",
    position: "flex-start mt-[-10px]",
  },
  [ArrowPosition.Bottom]: {
    file: ArrowTypes.Bottom,
    transform: "scale(.8)",
    position: "flex-end",
  },
  [ArrowPosition.TopRight]: {
    file: ArrowTypes.TopRight,
    transform: "",
    position: "self-end",
  },
  [ArrowPosition.TopLeft]: {
    file: ArrowTypes.TopLeft,
    transform: "",
    position: "self-start",
  },
  [ArrowPosition.BottomRight]: {
    file: ArrowTypes.BottomRight,
    transform: "",
    position: "self-end",
  },
  [ArrowPosition.BottomLeft]: {
    file: ArrowTypes.BottomLeft,
    transform: "",
    position: "self-start",
  },
};

interface StickyProps {
  key: string;
  content: React.ReactNode;
  offsetX: number;
  offsetY: number;
  rotation?: number;
  arrowPosition?: ArrowPosition;
  isFixed?: boolean;
  onExit?: () => void;
}

interface ArrowProps {
  arrowConfig: ArrowConfig;
  arrowPosition: ArrowPosition;
}

const isTopArrow = (arrowPosition: ArrowPosition) => {
  return arrowPosition === ArrowPosition.Top || arrowPosition === ArrowPosition.TopRight || arrowPosition === ArrowPosition.TopLeft;
};
const isVerticalArrow = (arrowPosition: ArrowPosition) => {
  return arrowPosition === ArrowPosition.Top || arrowPosition === ArrowPosition.Bottom;
};

function StickyArrow({ arrowConfig, arrowPosition }: ArrowProps) {
  return (
    <div
      className={`sticky-arrow ${arrowConfig.position}`}
      style={{ transform: arrowConfig.transform }}
    >
      <InlineSVG
        src={`/assets/${arrowConfig.file}`}
        title={`arrow pointing ${arrowPosition} direction`}
      />
    </div>
  );
}

const StickyNoteContainer = styled.div<{ $isVertical: boolean; $isFixed: boolean }>`
  width: 250px;
  height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${(props: any) => (props.$isVertical ? (props.$isTop ? "flex-start" : "flex-end") : "space-between")};
  position: ${(props: any) => (props.$isFixed ? "fixed" : "absolute")};
  z-index: 110000;
  isolation: isolate;
  font-family: "Mansalva";
  color: black;
  padding: 20px;
  font-size: 20px;
  line-height: 1.5;
  box-sizing: border-box;

  p {
    padding-bottom: 10px;
  }

  strong {
    color: var(--primary-cta);
    font-weight: 400;
  }
`;

const StickyNoteShadow = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  filter: drop-shadow(0 4px 3px rgba(0, 0, 0, 0.32));
`;

const StickyNoteSurface = styled.div<{ $cornerFolded: boolean }>`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(to bottom, #fffae2 0, #fffae2 5px, transparent 5px),
    linear-gradient(to bottom, rgba(255, 246, 202, 0), #ffefa8),
    #fff6ca;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
  clip-path: ${(props) =>
    props.$cornerFolded
      ? "polygon(0 0, calc(100% - 40px) 0, 100% 40px, 100% 100%, 0 100%)"
      : "polygon(0 0, 100% 0, 100% 100%, 0 100%)"};
  transition: clip-path 180ms ease-out;
`;

const StickyExitCorner = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  display: flex;
  width: 40px;
  height: 40px;
  box-sizing: border-box;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 0;
  perspective: 120px;
`;

const StickyCornerFold = styled.div<{ $folded: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 3;
  background: ${(props) =>
    props.$folded
      ? "linear-gradient(135deg, #f8edba 0%, #d6c16d 100%)"
      : "linear-gradient(to bottom, #fffae2 0, #fffae2 5px, #fff6ca 5px, #fff6ca 100%)"};
  clip-path: polygon(0 0, 100% 0, 100% 100%);
  filter: ${(props) => (props.$folded ? "drop-shadow(2px 2px 2px rgba(52, 47, 33, 0.22))" : "none")};
  pointer-events: none;
  transform: ${(props) => (props.$folded ? "rotate3d(1, 1, 0, 180deg)" : "rotate3d(1, 1, 0, 0deg)")};
  transform-origin: center;
  transform-style: preserve-3d;
  transition:
    transform 180ms ease-out,
    background 180ms ease-out,
    filter 180ms ease-out;
`;

const StickyExitButton = styled.button<{ $visible: boolean }>`
  position: relative;
  z-index: 4;
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0;
  appearance: none;
  border: 0 !important;
  border-radius: 0;
  background: none !important;
  box-shadow: none !important;
  color: #342f21 !important;
  cursor: pointer;
  font-family: Arial, sans-serif;
  font-size: 18px;
  line-height: 1;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  pointer-events: ${(props) => (props.$visible ? "auto" : "none")};
  transition: opacity 120ms ease-out;

  &:focus-visible {
    opacity: 1;
    outline: 2px solid var(--primary-cta);
    outline-offset: 1px;
  }
`;

function StickyNoteExitControl({
  onExit,
  isCornerFolded,
  setIsCornerFolded,
}: {
  onExit: () => void;
  isCornerFolded: boolean;
  setIsCornerFolded: (folded: boolean) => void;
}) {
  return (
    <StickyExitCorner
      data-testid="sticky-tour-exit-corner"
      onMouseEnter={() => setIsCornerFolded(true)}
      onMouseLeave={() => setIsCornerFolded(false)}
      onFocusCapture={() => setIsCornerFolded(true)}
      onBlurCapture={() => setIsCornerFolded(false)}
    >
      <SudoTooltip
        text="Exit tour"
        position="top"
        hasArrow={false}
        disabled={!isCornerFolded}
        triggerAs="span"
        triggerClassName="relative z-[4] block"
        tooltipClassName="!rounded-md !px-2 !py-1 whitespace-nowrap"
        tooltipStyle={{ transform: "translateY(-6px)" }}
      >
        <StickyExitButton
          type="button"
          aria-label="Exit tour"
          data-sticky-tour-exit
          $visible={isCornerFolded}
          onClick={onExit}
        >
          ×
        </StickyExitButton>
      </SudoTooltip>
      <StickyCornerFold
        aria-hidden
        data-testid="sticky-tour-corner-fold"
        $folded={isCornerFolded}
      />
    </StickyExitCorner>
  );
}

export default function StickyNote({ content, offsetX, offsetY, rotation, arrowPosition = ArrowPosition.None, isFixed = false, onExit }: StickyProps) {
  const [isExitCornerFolded, setIsExitCornerFolded] = useState(false);
  const arrowConfig = arrowPosition && arrowPosition !== ArrowPosition.None ? ARROW_CONFIGS[arrowPosition] : null;

  const positionStyle = isFixed
    ? {
        bottom: `${offsetY}px`,
        left: "50%",
        transform: `translateX(-50%) rotate(${rotation || 0}deg)`,
      }
    : {
        top: `${offsetY}px`,
        left: `${offsetX}px`,
        transform: `rotate(${rotation || 0}deg)`,
      };

  return (
    <StickyNoteContainer
      className="sticky-tour-sticky"
      $isVertical={isVerticalArrow(arrowPosition)}
      $isTop={isTopArrow(arrowPosition)}
      $isFixed={isFixed}
      style={positionStyle}
    >
      <StickyNoteShadow data-testid="sticky-tour-shadow">
        <StickyNoteSurface
          data-testid="sticky-tour-surface"
          $cornerFolded={Boolean(onExit && isExitCornerFolded)}
        />
      </StickyNoteShadow>
      {onExit && (
        <StickyNoteExitControl
          onExit={onExit}
          isCornerFolded={isExitCornerFolded}
          setIsCornerFolded={setIsExitCornerFolded}
        />
      )}
      {arrowConfig && isTopArrow(arrowPosition) && (
        <StickyArrow
          arrowConfig={arrowConfig}
          arrowPosition={arrowPosition}
        />
      )}

      <div className="w-full">{content}</div>

      {arrowConfig && !isTopArrow(arrowPosition) && (
        <StickyArrow
          arrowConfig={arrowConfig}
          arrowPosition={arrowPosition}
        />
      )}
    </StickyNoteContainer>
  );
}
